import React, { useEffect, useState, useRef, useCallback } from "react";
import "./App.css";
import { RetellWebClient } from "retell-client-js-sdk";
import { Phone, PhoneOff, Mic, MicOff } from 'lucide-react';
// Import the audio file from src/assets
import ringtone from './assets/xylophone_iphone.mp3';

interface TriageCallResponse {
  success: boolean;
  message: string;
  call_id: string;
  access_token: string;
  triage_id: string;
}

const retellWebClient = new RetellWebClient();

const App = () => {
  const [isCalling, setIsCalling] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [showConsent, setShowConsent] = useState(true);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<string>('');
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const isConnectingRef = useRef<boolean>(false); // Prevent duplicate connection attempts

  // Retrieve patient info, encounter_id and triage_id from URL path and query parameters
  const getPatientInfoFromURL = () => {
    const params = new URLSearchParams(window.location.search);
    const pathname = window.location.pathname;
    
    // Extract encounter_id and triage_id from URL path
    // Expected format: /triage/{encounter_id}/{triage_id}
    let encounter_id: string | null = null;
    let triage_id: string | null = null;
    const pathParts = pathname.split('/').filter(part => part !== ''); // Remove empty parts
    
    const triageIndex = pathParts.indexOf('triage');
    
    // Primary format: /triage/{encounter_id}/{triage_id}
    // The actual URL is: https://agent.nossana.com/triage/DNCEM7DLDERUES22/JESHT204E7AUDKCY
    // First ID is encounter_id, second ID is triage_id
    if (triageIndex !== -1 && pathParts.length > triageIndex + 2) {
      // After 'triage', the next part is encounter_id
      encounter_id = pathParts[triageIndex + 1];
      // The one after that is triage_id
      triage_id = pathParts[triageIndex + 2];
    }
    
    // Fallback: Check for /call/triage/{encounter_id}/{triage_id} format
    if (!encounter_id || !triage_id) {
      const callIndex = pathParts.indexOf('call');
      if (callIndex !== -1 && triageIndex !== -1 && triageIndex === callIndex + 1) {
        // After 'triage', the next part is encounter_id, and the one after that is triage_id
        if (pathParts.length > triageIndex + 1) {
          encounter_id = encounter_id || pathParts[triageIndex + 1];
        }
        if (pathParts.length > triageIndex + 2) {
          triage_id = triage_id || pathParts[triageIndex + 2];
        }
      }
    }
    
    // Fallback: Check for /encounters/{encounter_id}/triage/{triage_id} format
    if (!encounter_id || !triage_id) {
      const encountersIndex = pathParts.indexOf('encounters');
      if (encountersIndex !== -1 && pathParts.length > encountersIndex + 1) {
        encounter_id = encounter_id || pathParts[encountersIndex + 1];
      }
      if (triageIndex !== -1 && pathParts.length > triageIndex + 1) {
        triage_id = triage_id || pathParts[triageIndex + 1];
      }
    }
    
    // Fallback to query parameters if not in path
    if (!encounter_id) {
      encounter_id = params.get('encounter_id');
    }
    if (!triage_id) {
      triage_id = params.get('triage_id');
    }
    
    return {
      name: params.get('patient') || 'DefaultPatient',
      email: params.get('email') || 'default@email.com',
      drname: params.get('drname') || 'Not Specified',
      agent: params.get('agent') || 'Not Specified',
      encounter_id: encounter_id,
      triage_id: triage_id
    };
  };

  const patientInfo = getPatientInfoFromURL();
  console.log('Patient Info:', patientInfo);

  // Function to register triage call and get access token
  const registerTriageCall = useCallback(async (encounterId: string, triageId: string): Promise<TriageCallResponse> => {
    try {
      // Use environment variable for API base URL (localhost:8000 for dev, api.nossana.com for production)
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const url = `${apiBaseUrl}/api/v1/provider/encounters/${encounterId}/triage/${triageId}/register`;
      
      console.log('Registering triage call via API');
      console.log('Encounter ID:', encounterId);
      console.log('Triage ID:', triageId);
      console.log('API URL:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        console.error('Error response:', errorData);
        
        if (response.status === 404) {
          throw new Error(`Encounter not found: ${errorData.detail || 'The encounter does not exist'}`);
        } else if (response.status === 400) {
          throw new Error(`Bad request: ${errorData.detail || 'Invalid encounter or triage ID'}`);
        } else if (response.status === 500) {
          throw new Error(`Server error: ${errorData.detail || 'Failed to register triage call'}`);
        } else {
          throw new Error(`API error (${response.status}): ${errorData.detail || 'Unknown error'}`);
        }
      }

      const data: TriageCallResponse = await response.json();
      console.log('Triage call registered successfully:', data);

      // Validate response structure
      if (!data.success || !data.access_token || !data.call_id) {
        console.error('Invalid response structure:', data);
        throw new Error('Invalid response from API: missing required fields');
      }

      return data;
    } catch (err) {
      console.error('Error in registerTriageCall:', err);
      if (err instanceof Error) {
        throw err;
      }
      throw new Error('Unknown error occurred while registering triage call');
    }
  }, []);


  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (isCalling) {
      interval = setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isCalling]);

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Initialize the SDK and audio
  useEffect(() => {
    // Create audio element with proper error handling
    try {
      audioRef.current = new Audio(ringtone);
      audioRef.current.loop = true;
      
      // Add error event listener with detailed logging
      audioRef.current.addEventListener('error', (e) => {
        const audioElement = e.target as HTMLAudioElement;
        console.error('Audio error details:', {
          error: audioElement.error,
          networkState: audioElement.networkState,
          readyState: audioElement.readyState,
          src: audioElement.src
        });
        setAudioError('Failed to load audio file. Please check console for details.');
      });

      // Add load event listener to confirm successful loading
      audioRef.current.addEventListener('loadeddata', () => {
        console.log('Audio file loaded successfully');
        setAudioError(null);
      });
    } catch (error) {
      console.error('Error initializing audio:', error);
      setAudioError('Failed to initialize audio');
    }

    // RetellWebClient event listeners
    retellWebClient.on("call_started", () => {
      console.log("call started");
    });
    
    retellWebClient.on("call_ended", () => {
      console.log("call ended");
      setIsCalling(false);
    });
    
    retellWebClient.on("agent_start_talking", () => {
      console.log("agent_start_talking");
    });
    
    retellWebClient.on("agent_stop_talking", () => {
      console.log("agent_stop_talking");
    });
    
    retellWebClient.on("audio", (audio) => {
      // console.log(audio);
    });
    
    retellWebClient.on("update", (update) => {
      // console.log(update);
    });
    
    retellWebClient.on("metadata", (metadata) => {
      // console.log(metadata);
    });
    
    retellWebClient.on("error", async (error) => {
      console.error("An error occurred in RetellWebClient:", error);
      
      // Prevent duplicate reconnection attempts
      if (isConnectingRef.current) {
        console.log('Reconnection already in progress');
        return;
      }
      
      // On any error, try to get a new token and reconnect
      const patientInfo = getPatientInfoFromURL();
      if (patientInfo.encounter_id && patientInfo.triage_id) {
        console.log('Error detected. Getting new token and reconnecting...');
        setIsConnecting(true);
        isConnectingRef.current = true;
        setConnectionStatus('Reconnecting...');
        
        try {
          // Re-register the triage call to get a new token
          const newTokenResponse = await registerTriageCall(patientInfo.encounter_id, patientInfo.triage_id);
          
          if (newTokenResponse && newTokenResponse.success && newTokenResponse.access_token) {
            console.log('New token retrieved. Restarting call...');
            setConnectionStatus('Reconnecting to agent...');
            // Stop the current failed call
            retellWebClient.stopCall();
            setIsCalling(false);
            
            // Wait a moment before retrying
            setTimeout(async () => {
              try {
                await retellWebClient.startCall({
                  accessToken: newTokenResponse.access_token,
                });
                setIsCalling(true);
                setIsConnecting(false);
                isConnectingRef.current = false;
                setConnectionStatus('');
                console.log('Call restarted successfully');
              } catch (retryError) {
                console.error('Failed to restart call:', retryError);
                setIsConnecting(false);
                isConnectingRef.current = false;
                setConnectionStatus('');
                alert('Failed to restart call. Please try again.');
              }
            }, 1000);
            return;
          }
        } catch (refreshError) {
          console.error('Error getting new token:', refreshError);
          setIsConnecting(false);
          isConnectingRef.current = false;
          setConnectionStatus('');
        }
      }
      
      // If refresh failed, stop the call
      retellWebClient.stopCall();
      setIsCalling(false);
      setIsConnecting(false);
      isConnectingRef.current = false;
      setConnectionStatus('');
    });

    // Cleanup function to stop audio when component unmounts
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current.removeEventListener('error', () => {});
        audioRef.current.removeEventListener('loadeddata', () => {});
      }
    };
  }, [registerTriageCall]);

  const handleConsent = async () => {
    // Prevent duplicate connection attempts
    if (isConnectingRef.current || isCalling) {
      console.log('Connection already in progress or call already active');
      return;
    }

    setShowConsent(false);
    setIsConnecting(true);
    isConnectingRef.current = true;
    setConnectionStatus('Connecting to call...');
    
    // Start playing audio after user interaction
    if (audioRef.current) {
      try {
        // Ensure audio is loaded before playing
        if (audioRef.current.readyState >= 2) {
          await audioRef.current.play();
          setAudioError(null);
        } else {
          setAudioError('Audio is still loading. Please try again.');
        }
      } catch (error) {
        console.error('Error playing audio:', error);
        setAudioError('Failed to play audio. Please check console for details.');
      }
    }

    // Get encounter_id and triage_id from URL and start the call immediately
    const patientInfo = getPatientInfoFromURL();
    
    // Ensure encounter_id and triage_id are present
    if (!patientInfo.encounter_id || !patientInfo.triage_id) {
      console.error('Missing required IDs in URL');
      console.error('Current URL:', window.location.href);
      console.error('Pathname:', window.location.pathname);
      console.error('Encounter ID:', patientInfo.encounter_id);
      console.error('Triage ID:', patientInfo.triage_id);
      setIsConnecting(false);
      isConnectingRef.current = false;
      setConnectionStatus('');
      alert(`Error: Missing required IDs in URL.\n\nEncounter ID: ${patientInfo.encounter_id || 'Not found'}\nTriage ID: ${patientInfo.triage_id || 'Not found'}\n\nExpected format: /call/triage/{encounter_id}/{triage_id}`);
      return;
    }

    try {
      console.log('Starting call process after consent...');
      setConnectionStatus('Registering call...');
      console.log('Registering triage call for encounter:', patientInfo.encounter_id, 'triage:', patientInfo.triage_id);
      
      // Register triage call and get access token
      const triageCallResponse = await registerTriageCall(patientInfo.encounter_id, patientInfo.triage_id);
      console.log('Triage Call Response received:', triageCallResponse);
      
      if (triageCallResponse && triageCallResponse.success && triageCallResponse.access_token) {
        console.log('Access token found, starting call...');
        setConnectionStatus('Connecting to agent...');
        console.log('Call ID:', triageCallResponse.call_id);
        
        // Stop the ringtone when call starts
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        }
        
        // Connect to the call using the access token
        await retellWebClient.startCall({
          accessToken: triageCallResponse.access_token,
        });
        setIsCalling(true);
        setIsConnecting(false);
        isConnectingRef.current = false;
        setConnectionStatus('');
        console.log('Call started successfully');
      } else {
        console.error('Invalid response from triage API:', triageCallResponse);
        const errorMsg = triageCallResponse?.message || 'No access token received from server';
        setIsConnecting(false);
        isConnectingRef.current = false;
        setConnectionStatus('');
        alert('Error: ' + errorMsg + '\n\nResponse: ' + JSON.stringify(triageCallResponse));
      }
    } catch (error) {
      console.error('Error starting call after consent:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      setIsConnecting(false);
      isConnectingRef.current = false;
      setConnectionStatus('');
      alert('Failed to start call. Error: ' + errorMessage + '\n\nPlease check the console for more details.');
    }
  };

  const toggleConversation = async () => {
    console.log('Button clicked - toggleConversation called');
    console.log('Current state - isCalling:', isCalling);
    console.log('Patient info:', patientInfo);
    
    // Stop the ringtone when call starts
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    if (isCalling) {
      console.log('Stopping call...');
      retellWebClient.stopCall();
      setIsCalling(false);
      isConnectingRef.current = false;
      setConnectionStatus('');
    } else {
      // Prevent duplicate connection attempts
      if (isConnectingRef.current) {
        console.log('Connection already in progress');
        return;
      }

      // Ensure encounter_id and triage_id are present
      if (!patientInfo.encounter_id || !patientInfo.triage_id) {
        console.error('Missing required IDs in URL');
        console.error('Current URL:', window.location.href);
        console.error('Pathname:', window.location.pathname);
        console.error('Encounter ID:', patientInfo.encounter_id);
        console.error('Triage ID:', patientInfo.triage_id);
        alert(`Error: Missing required IDs in URL.\n\nEncounter ID: ${patientInfo.encounter_id || 'Not found'}\nTriage ID: ${patientInfo.triage_id || 'Not found'}\n\nExpected format: /call/triage/{encounter_id}/{triage_id}`);
        return;
      }

      setIsConnecting(true);
      isConnectingRef.current = true;
      setConnectionStatus('Connecting to call...');

      try {
        console.log('Starting call process...');
        setConnectionStatus('Registering call...');
        console.log('Registering triage call for encounter:', patientInfo.encounter_id, 'triage:', patientInfo.triage_id);
        
        // Register triage call and get access token
        const triageCallResponse = await registerTriageCall(patientInfo.encounter_id, patientInfo.triage_id);
        console.log('Triage Call Response received:', triageCallResponse);
        
        if (triageCallResponse && triageCallResponse.success && triageCallResponse.access_token) {
          console.log('Access token found, starting call...');
          setConnectionStatus('Connecting to agent...');
          console.log('Call ID:', triageCallResponse.call_id);
          
          // Connect to the call using the access token
          await retellWebClient.startCall({
            accessToken: triageCallResponse.access_token,
          });
          setIsCalling(true);
          setIsConnecting(false);
          isConnectingRef.current = false;
          setConnectionStatus('');
          console.log('Call started successfully');
        } else {
          console.error('Invalid response from triage API:', triageCallResponse);
          const errorMsg = triageCallResponse?.message || 'No access token received from server';
          setIsConnecting(false);
          isConnectingRef.current = false;
          setConnectionStatus('');
          alert('Error: ' + errorMsg + '\n\nResponse: ' + JSON.stringify(triageCallResponse));
        }
      } catch (error) {
        console.error('Error in toggleConversation:', error);
        const errorMessage = error instanceof Error ? error.message : String(error);
        setIsConnecting(false);
        isConnectingRef.current = false;
        setConnectionStatus('');
        alert('Failed to start call. Error: ' + errorMessage + '\n\nPlease check the console for more details.');
      }
    }
  };

  return (
    <div className="app-container">
      {showConsent && (
        <div className="consent-banner">
          <p>We use cookies and audio to enhance your experience. By clicking "Accept", you agree to our use of cookies and audio features.</p>
          <button onClick={handleConsent} className="consent-button">Accept</button>
          {audioError && <p className="error-message">{audioError}</p>}
        </div>
      )}
      <div className="phone-container">
         

        <header className="phone-header">
          <div className="header">
            {!isCalling && <p className="phoneNumber">Nossana Voice Agent</p>}
            {!isCalling && <p className="calling">Ringing...</p>}
          </div>
        </header>
        <div className="phone-content">
          {isCalling ? (
            <div className="call-screen">
              <div className="caller-name" style={{ fontSize: '30px' }}>Nossana Voice Agent</div>
              <div className="call-duration" style={{ fontSize: '20px', fontWeight: 'lighter' }}>{formatTime(callDuration)}</div>
              <div className="calling" style={{ fontSize: '30px' }}>In Call</div>

              <div className="button-container">
                <button
                  onClick={toggleMute}
                  className={`call-button mute-button ${isMuted ? 'muted' : ''}`}
                >
                  {isMuted ? <MicOff size={54} /> : <Mic size={24} />}
                </button>
                <button
                  onClick={toggleConversation}
                  className="call-button end-call-button"
                >
                  <PhoneOff size={24} />
                </button>
              </div>
            </div>
          ) : isConnecting ? (
            <div className="connecting-screen">
              <div className="loader-container">
                <div className="loader"></div>
              </div>
              <div className="connection-status">{connectionStatus || 'Connecting...'}</div>
              <div className="connection-message">Please wait while we connect you to the call</div>
            </div>
          ) : (
            <div className="start-call-container">
              <button
                onClick={toggleConversation}
                className="call-button start-call-button"
                disabled={isConnecting}
              >
                <Phone size={32} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default App;
